import { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { presetRange } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

export function DateRangeFilter({ value, onChange, testid = "date-range" }) {
  const { bizDate } = useAuth();
  const setPreset = (p) => onChange(p === "custom" ? { ...value, preset: p } : { preset: p, ...presetRange(p, bizDate) });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <Select value={value.preset} onValueChange={setPreset}>
        <SelectTrigger className="h-9 w-40" data-testid={`${testid}-preset`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="today">Today</SelectItem>
          <SelectItem value="yesterday">Yesterday</SelectItem>
          <SelectItem value="week">This Week</SelectItem>
          <SelectItem value="month">This Month</SelectItem>
          <SelectItem value="custom">Custom Range</SelectItem>
        </SelectContent>
      </Select>
      {value.preset === "custom" && (
        <>
          <Input type="date" className="h-9 w-40" value={value.from} onChange={(e) => onChange({ ...value, from: e.target.value })} data-testid={`${testid}-from`} />
          <span className="text-slate-400">→</span>
          <Input type="date" className="h-9 w-40" value={value.to} onChange={(e) => onChange({ ...value, to: e.target.value })} data-testid={`${testid}-to`} />
        </>
      )}
    </div>
  );
}

export function useRange(preset = "today") {
  const { bizDate } = useAuth();
  return useState({ preset, ...presetRange(preset, bizDate) });
}

let salesmenCache = null;
export function useSalesmen(reloadKey) {
  const [rows, setRows] = useState(salesmenCache || []);
  useEffect(() => {
    api.get("/salesmen").then((r) => { salesmenCache = r.data; setRows(r.data); }).catch(() => {});
  }, [reloadKey]);
  return rows;
}

export function SalesmanSelect({ value, onChange, allowAll = false, activeOnly = false, testid = "salesman-select", className = "w-56" }) {
  const rows = useSalesmen().filter((s) => !activeOnly || s.status === "ACTIVE");
  return (
    <Select value={value || (allowAll ? "all" : undefined)} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
      <SelectTrigger className={`h-9 ${className}`} data-testid={testid}><SelectValue placeholder="Select salesman" /></SelectTrigger>
      <SelectContent>
        {allowAll && <SelectItem value="all">All salesmen</SelectItem>}
        {rows.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`${testid}-option-${s.code}`}>{s.full_name} ({s.code})</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function useProducts(activeOnly = true) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { active_only: activeOnly } }).then((r) => setRows(r.data)).catch(() => {});
  }, [activeOnly]);
  return rows;
}

export function ProductSelect({ value, onChange, products, testid = "product-select", className = "w-full" }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={`h-10 ${className}`} data-testid={testid}><SelectValue placeholder="Select product" /></SelectTrigger>
      <SelectContent>
        {products.map((p) => (
          <SelectItem key={p.id || p.product_id} value={p.id || p.product_id} data-testid={`${testid}-option-${p.sku}`}>
            {p.name} · {p.sku}{p.movable !== undefined ? ` · ${p.movable} pks` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
